import React, { useState, useEffect } from 'react';
import { Button, Form, Input, notification, Table, Col, Row } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { getOnBoardingDataThunk, verifyPan, generateEmpBureau } from '../../../redux/slice/preOnBoarding';
import { CheckCircleOutlined } from '@ant-design/icons';
import { FaCheck, FaTimes } from 'react-icons/fa';
import moment from 'moment';
import { useAuth } from "../../../context/AuthContext";
import { HashLoader } from 'react-spinners';
import { trackFeatureAction } from "../../../analytics/googleAnalytics";

const BureauCheck = () => {
  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [panVerified, setPanVerified] = useState(false);
  const [pan, setPan] = useState('');
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [state, setState] = useState('');
  const [pincode, setPincode] = useState('');
  const [dob, setDob] = useState('');
  const [city, setCity] = useState('');
  const [address, setAddress] = useState('');


  const dispatch = useDispatch();
  const { authState } = useAuth();
  const jwtBearer = authState.token;


  const [form] = Form.useForm();
  const onboardingData = useSelector((state) => state?.preOnBoardingSlice?.onBoardingFormdata);
  const isLoadingOnBoardingData = useSelector((state) => state?.preOnBoardingSlice?.isLoading);
  const isErrorOnBoardingData = useSelector((state) => state?.preOnBoardingSlice?.isErrorOnBoardingData);

  useEffect(() => {
    trackFeatureAction(
      "PreOnboarding",
      "Employee Bureau",
      "open"
    );
  }, []);

  useEffect(() => {
    const verifyPhone = async () => {
      if (phoneNumber.length === 10) {
        setLoading(true);
        try {
          const response = await dispatch(getOnBoardingDataThunk({ jwtBearer }));
          console.log("response", response);

          if (response?.payload?.data?.length > 0) {
            const user = response.payload.data.find(item => item.phone === phoneNumber);

            if (user) {
              if (!user.consent_given) {
                notification.warning({
                  message: 'Consent Not Given',
                  description: 'The employee has not given consent yet.',
                  placement: 'topRight',
                });
              } else {
                setPhoneVerified(true);
                form.setFieldsValue({ name: user.name });
              }
            } else {
              setPhoneVerified(false);
              form.setFieldsValue({ name: '' });
              notification.warning({
                message: 'Phone Not Found',
                description: 'The entered phone number is not present in our system.',
                placement: 'topRight',
              });
            }
          } else {
            setPhoneVerified(false);
            form.setFieldsValue({ name: '' });
          }
        } catch (error) {
          notification.error({
            message: 'Error',
            description: 'There was an issue while verifying the phone number.',
            placement: 'topRight',
          });
        } finally {
          setLoading(false);
        }
      } else {
        setPhoneVerified(false);
        form.setFieldsValue({ name: '' });
      }
    };

    verifyPhone();
  }, [phoneNumber, dispatch, form]);

  const handleSubmit = async (values) => {
    setLoading(true);
    const requestBody = {
      name: values.name,
      pan_card: values.pan,
      dob: moment(values.dob).format("DD-MM-YYYY"),
      number: values.phone,
      residence_address: values.address,
      city: values.city,
      pincode: values.pincode,
      state: values.state,
    };
    console.log('CRIF Request - ', requestBody);

    try {
      const response = await dispatch(
        generateEmpBureau({
          requestBody,
          jwtBearer,
        })
      );

      if (response.payload.code === 400) {
        // existing error handling
      } else {
        trackFeatureAction(
          "PreOnboarding",
          "Employee Bureau",
          "fetch_bureau"
        );

        notification.success({
          message: "Success",
          description:
            `Employee Bureau has been generated successfully. Score: ${response.payload.bureau_score}`,
          placement: "topRight",
        });

        const onboardingResponse = await dispatch(
          getOnBoardingDataThunk({ jwtBearer })
        );

        handleReset();
      }
    } catch (error) {
      notification.error({
        message: 'Error',
        description: error?.payload?.message || 'There was an issue fetching the bureau data.',
        placement: 'topRight',
      });
    } finally {
      setLoading(false);
    }
  };


  const handleFormChange = (_, allValues) => {
    setIsFormChanged(Object.values(allValues).some((value) => value));
  };

  const handleReset = () => {
    trackFeatureAction(
      "PreOnboarding",
      "Employee Bureau",
      "reset"
    );
    form.resetFields();
    setPhoneVerified(false);
    setName('');
    setPhoneNumber('');
    setPanVerified(false);
    setPan('');
    setState('');
    setPincode('');
    setCity('');
    
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      align: 'center',
      render: text => <span style={{ fontSize: '16px' }}>{text}</span>,
    },
    {
      title: "Mobile No.",
      dataIndex: "phone",
      key: "phone",
      align: 'center',
      render: text => <span style={{ fontSize: '16px' }}>{text}</span>,
    },
    {
      title: "Bureau Generation Date",
      dataIndex: "bureau_generation_date",
      key: "bureau_generation_date",
      align: 'center',
      render: text => <span style={{ fontSize: '16px' }}>{text}</span>,
    },
    {
      title: "Bureau Score",
      dataIndex: "bureau_score",
      key: "bureau_score",
      align: 'center',
      render: text => <span style={{ fontSize: '16px' }}>{text}</span>,
    },

  ];


  const handlePanVerification = async () => {
    setVerifyLoading(true)
    try {
      const panResponse = await dispatch(verifyPan({ pan, jwtBearer }));

      console.log("panResponse", panResponse);

      if (panResponse.payload.success) {
        const panDetails = panResponse.payload.data?.panDetails;

        if (panDetails) {
          // Check if the PAN is verified
          if (panDetails.verified === true && panDetails.details.nameMatch) {
            trackFeatureAction(
              "PreOnboarding",
              "Employee Bureau",
              "verify_pan",
              {
                status: "success",
              }
            );
            setPanVerified(true);
            notification.success({
              message: 'PAN Verified',
              description: `PAN is successfully verified. Name: ${panDetails.details.firstName} ${panDetails.details.lastName}`,
              placement: 'topRight',
            });
          } else if (panDetails.verified === false) {
            trackFeatureAction(
              "PreOnboarding",
              "Employee Bureau",
              "verify_pan",
              {
                status: "failed",
              }
            );
            setPanVerified(false);
            notification.error({
              message: 'Invalid PAN Number',
              description: 'Please enter valid PAN number.',
              placement: 'topRight',
            });
          }
        } else {
          setPanVerified(false);
          notification.error({
            message: 'PAN Details Missing',
            description: 'There was an issue retrieving PAN details.',
            placement: 'topRight',
          });
        }
      } else {
        setPanVerified(false);
        notification.error({
          message: 'PAN Verification Failed',
          description: panResponse.payload.errorMessage || 'There was an issue verifying the PAN.',
          placement: 'topRight',
        });
      }
    } catch (error) {
      setPanVerified(false);
      notification.error({
        message: 'Error',
        description: 'There was an issue while verifying the PAN number.',
        placement: 'topRight',
      });
    } finally {
      setVerifyLoading(false);
    }

  };

  const last30DaysData = onboardingData?.filter(item => {
    const updatedAt = moment(item.updated_at);
    return updatedAt.isAfter(moment().subtract(30, 'days'));
  });



  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      paddingTop: '20px',
      paddingRight: '45px',
      paddingLeft: '45px',
      backgroundColor: '#f1f1f1',
      alignItems: 'center'
    }}>
      <Form
        form={form}
        style={{
          width: '100%',
          paddingTop: '30px',
          paddingBottom: '30px',
          paddingLeft: '30px',
          paddingRight: '30px',
          border: '1px solid #ddd',
          borderRadius: '12px',
          backgroundColor: '#ffffff',
          boxShadow: '0 6px 15px rgba(0, 0, 0, 0.1)',
        }}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleFormChange}
      >
        <div className="container-fluid">
          <Row gutter={48} className="custom-form2">
            <style>
              {`

      .custom-form2 .ant-form-item-label > label {
        font-size: 16px !important; 
        font-weight: 600 !important;
      }
    `}
            </style>
            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
              <Form.Item
                label="Phone Number"
                name="phone"
                initialValue={phoneNumber}
                rules={[
                  { required: true, message: 'Please input phone number!' },
                  {
                    pattern: /^[0-9]{10}$/,
                    message: 'Phone number must be 10 digits.',
                  },
                ]}
              >
                <Input
                  placeholder="Enter 10-digit phone number"
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  value={phoneNumber}
                  disabled={phoneVerified}
                  maxLength={10}
                  onInput={(e) => {
                    // Allow only numeric input by removing any non-numeric characters
                    e.target.value = e.target.value.replace(/\D/g, '');
                  }}
                  suffix={
                    phoneVerified ? (
                      <CheckCircleOutlined
                        style={{
                          color: 'green',
                          fontSize: '16px',
                        }}
                      />
                    ) : null
                  }
                  style={{
                    height: '40px',
                    backgroundColor: phoneVerified ? 'white' : '',
                    color: phoneVerified ? '#000' : '',
                    cursor: phoneVerified ? 'text' : '',
                    border: '1px solid #d9d9d9',
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
              <Form.Item
                label="Candidate's Name"
                name="name"
                rules={[
                  { required: true, message: "Please input candidate's name!" },
                  {
                  },
                ]}
              >
                <Input
                  value={name}
                  disabled={phoneVerified}
                  placeholder="Enter candidate's name"
                  style={{
                    backgroundColor: phoneVerified ? 'white' : '',
                    color: phoneVerified ? '#000' : '',
                    cursor: phoneVerified ? 'text' : '',
                    border: '1px solid #d9d9d9',
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
              <Row gutter={20}>
                <Col xs={16} sm={16} md={16} lg={16} xl={16}>
                  <Form.Item
                    label="PAN"
                    name="pan"
                    rules={[
                      { required: true, message: 'Please input PAN!' },
                      {
                        pattern: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/,
                        message: 'Enter a valid PAN number',
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter PAN"
                      disabled={panVerified}
                      value={pan}
                      onChange={(e) => setPan(e.target.value)}
                      style={{
                        backgroundColor: phoneVerified ? 'white' : '',
                        color: phoneVerified ? '#000' : '',
                        cursor: phoneVerified ? 'text' : '',
                        border: '1px solid #d9d9d9',
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={6} sm={6} md={6} lg={6} xl={6} style={{ marginTop: '30px' }}>
                  <Form.Item>
                    <Button
                      type="primary"
                      block
                      onClick={handlePanVerification}
                      loading={verifyLoading}
                      disabled={panVerified || !pan}
                    >
                      {panVerified ? 'Verified' : 'Verify'}
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Col>

            <Col xs={24} sm={6} md={6} lg={6} xl={6}>
              <Form.Item
                label="Date of Birth"
                name="dob"
                rules={[{ required: true, message: 'Please select date of birth!' }]}
              >
                <Input type="date" placeholder="Select date of birth" />
              </Form.Item>
            </Col>



            <Col xs={12} sm={6} md={6}>
              <Form.Item
                name="pincode"
                label="Pincode"
                rules={[
                  { required: true, message: 'Please enter your pincode!' },
                  { pattern: /^[1-9][0-9]{5}$/, message: 'Please enter a valid 6-digit pincode!' },
                ]}
              >
                <Input placeholder="Enter pincode" maxLength={6} />
              </Form.Item>
            </Col>


            {/* City */}
            <Col xs={24} sm={6} md={6}>
              <Form.Item
                name="city"
                label="City"
                rules={[
                  { required: true, message: 'Please enter city!' },
                  { pattern: /^[a-zA-Z\s]*$/, message: 'City name cannot contain numbers or special characters.' }
                ]}
              >
                <Input placeholder="Enter city" />
              </Form.Item>
            </Col>

            {/* State */}
            <Col xs={12} sm={6} md={6}>
              <Form.Item
                name="state"
                label="State"
                rules={[
                  { required: true, message: 'Please enter state!' },
                  { pattern: /^[a-zA-Z\s]*$/, message: 'State name cannot contain numbers or special characters.' }
                ]}
              >
                <Input placeholder="Enter state" />
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} md={6} lg={6} xl={6}>
              <Form.Item
                label="Address"
                name="address"
                rules={[{ required: true, message: 'Please input address!' }]}
              >
                <Input.TextArea placeholder="Enter address" rows={1} />
              </Form.Item>
            </Col>


            {phoneVerified && (
              <Col xs={8} sm={8} md={4} lg={4} xl={4} style={{ marginTop: '30px' }}>
                <Form.Item>
                  <Button type="primary" htmlType="submit" block loading={loading}>
                    Fetch Bureau
                  </Button>
                </Form.Item>
              </Col>
            )}

            {isFormChanged && (
              <Col xs={8} sm={8} md={4} lg={4} xl={4} style={{ marginTop: '30px' }}>
                <Form.Item>
                  <Button type="default" block onClick={handleReset}>
                    Reset
                  </Button>
                </Form.Item>
              </Col>
            )}
          </Row>

        </div>
      </Form>


      {/* Table for displaying the onboarding data */}
      <div style={{ width: '100%', marginTop: '40px' }}>
        <Table
          columns={columns}
          dataSource={last30DaysData?.filter(item => item.bureau_score !== null) || []}
          rowKey="email"
          loading={isLoadingOnBoardingData}
          pagination={{ pageSize: 5 }}
          components={{
            header: {
              cell: (props) => {
                return (
                  <th {...props} style={{ fontSize: '18px', fontWeight: 'bold', textAlign: 'center' }}>
                    {props.children}
                  </th>
                );
              }
            }
          }}
        />
      </div>

    </div>

  );
};

export default BureauCheck;
